import {
  Grid,
  Text,
  Flex,
  Box,
  Popover,
  Stack,
  TextInput,
  Tooltip,
  Divider,
  Group,
  ActionIcon,
} from "@mantine/core"
import { IconSearch, IconChevronDown, IconX } from "@tabler/icons-react"
import { UseQueryResult } from "@tanstack/react-query"
import _ from "lodash"
import {
  useState,
  useMemo,
  useRef,
  useEffect,
  KeyboardEvent,
  ReactNode,
} from "react"

import { DropdownItemType } from "@costory/types/endpoints/DropdownTypes"

import { ICON_STROKE } from "@costory/shared/const"

import DropDownItem from "@costory/front/components/dropdown/DropDownItem"
import GroupPills from "@costory/front/components/dropdown/GroupPills"
import { ICON_SIZE } from "@costory/front/components/dropdown/constants"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"

export type BaseDropdownSelectorProps = {
  // Core props
  value: string
  onChange: (value: string) => void
  options: Array<{
    group: string
    items: DropdownItemType[]
  }>

  // UI customization
  label?: string
  placeholder?: string
  width?: number
  dropdownHeight?: number
  inputWidth?: number

  // Features
  clearable?: boolean
  showGroupPills?: boolean
  showAllPill?: boolean

  // Customization functions
  getIcon?: (value: string) => ReactNode
  renderRightPanel?: (selectedItem: DropdownItemType | undefined) => ReactNode
  customSearchFunction?: (item: DropdownItemType, query: string) => boolean

  // Special handling
  onGroupChange?: (group: string) => void
  hasSuggestedItems?: boolean
  suggestedQuery?: UseQueryResult<unknown, unknown> // For QueryWrapper

  // Styling
  leftSection?: ReactNode
  rightSection?: ReactNode
}

const BaseDropdownSelector = ({
  value,
  onChange,
  options,
  label = "Select",
  placeholder = "Select option",
  width = 750,
  dropdownHeight = 350,
  inputWidth = 250,
  clearable = false,
  showGroupPills = true,
  showAllPill = true,
  getIcon,
  renderRightPanel,
  customSearchFunction,
  onGroupChange,
  hasSuggestedItems = false,
  suggestedQuery,
  leftSection,
  rightSection,
}: BaseDropdownSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<string>("All")
  const [hoveredValue, setHoveredValue] = useState<string | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const allItems = options.flatMap((group) => group.items)
  const selectedItem = allItems.find((item) => item.value === value)

  const handleGroupChange = (newGroup: string) => {
    const finalGroup =
      newGroup === "Suggested" && !hasSuggestedItems ? "All" : newGroup
    setSelectedGroup(finalGroup)
    setHighlightedIndex(-1)
    setHoveredValue(null)
    onGroupChange?.(finalGroup)
    inputRef.current?.focus()
  }

  const filteredItems = useMemo(() => {
    const selectedGroupItems =
      selectedGroup === "All"
        ? allItems
        : options.find((group) => group.group === selectedGroup)?.items || []

    const sortedItems =
      selectedGroup === "Suggested"
        ? selectedGroupItems
        : _.orderBy(selectedGroupItems, ["isSuggested"], ["desc"])

    if (!searchQuery) return _.uniqBy(sortedItems, "value")
    const query = searchQuery.toLowerCase().trim()
    return _.uniqBy(
      sortedItems.filter(
        (item) =>
          item.label.toLowerCase().includes(query) ||
          item.value.toLowerCase().includes(query) ||
          (customSearchFunction && customSearchFunction(item, searchQuery)), // Pass original query for custom function
      ),
      "value",
    )
  }, [selectedGroup, allItems, options, searchQuery, customSearchFunction])

  const handleToggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen((prev) => !prev)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredItems.length === 0) return
    const { key } = event
    const isEnter = key === "Enter"
    const direction = key === "ArrowDown" ? 1 : key === "ArrowUp" ? -1 : 0
    if (!isEnter && direction === 0) return
    event.preventDefault()

    if (isEnter && highlightedIndex >= 0) {
      handleItemClick(filteredItems[highlightedIndex].value)
      return
    }

    const total = filteredItems.length
    const selectedIndex =
      highlightedIndex === -1
        ? filteredItems.findIndex((item) => item.value === value)
        : highlightedIndex

    const newIndex = (selectedIndex + direction + total) % total
    setHighlightedIndex(newIndex)
    setHoveredValue(filteredItems[newIndex].value)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    setSearchQuery(value)
    setIsSearching(true)
    setIsOpen(true)
    handleGroupChange("All")
  }

  const handleItemClick = (itemValue: string) => {
    onChange(itemValue)
    setSearchQuery("")
    setIsSearching(false)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange("")
    setSearchQuery("")
    setIsOpen(false)
  }
  const handleInputClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen((prev) => !prev)
    inputRef.current?.focus()
  }

  // Keep focus on input when menu is open
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("")
      setIsSearching(false)
      setHighlightedIndex(-1)
      return
    }
    const groupWithSelected = options.find((group) =>
      group.items.some((item) => item.value === value),
    )
    const isSelectedInSuggested =
      suggestedQuery &&
      Array.isArray(suggestedQuery.data) &&
      suggestedQuery.data.some(
        (axis) => axis.name === value && axis.category === "suggested",
      )

    const newSelectedGroup =
      isSelectedInSuggested && hasSuggestedItems
        ? "Suggested"
        : groupWithSelected
          ? groupWithSelected.group
          : "All"
    handleGroupChange(newSelectedGroup)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value, options])

  useEffect(() => {
    const el = scrollContainerRef.current?.querySelector<HTMLElement>(
      `[data-index="${highlightedIndex}"]`,
    )
    if (el && highlightedIndex >= 0) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" })
      })
    }
  }, [highlightedIndex])

  const renderDropDownItem = (item: DropdownItemType, idx: number) => (
    <DropDownItem
      key={`${selectedGroup}-${item.value}-${idx}`}
      item={item}
      index={idx}
      selectedValue={value}
      highlightedIndex={highlightedIndex}
      hoveredValue={hoveredValue}
      onHover={setHoveredValue}
      onClick={handleItemClick}
      menuItemStyle={{
        base: {
          transition: "transform 0.15s ease, background-color 0.15s ease",
          cursor: "pointer",
          borderRadius: 4,
          fontSize: 14,
        },
        selected: {
          backgroundColor: "var(--mantine-color-surfaceHover-0)",
        },
      }}
      groupKey={selectedGroup}
      getIcon={getIcon}
      hasSuggestedAxes={hasSuggestedItems}
    />
  )

  const defaultLeftSection = leftSection || (
    <IconSearch
      size={ICON_SIZE}
      stroke={ICON_STROKE}
      onClick={handleToggleOpen}
    />
  )

  const defaultRightSection = rightSection || (
    <Group gap={2} wrap="nowrap" align="center" pr={10}>
      {(searchQuery || selectedItem?.label) && clearable && (
        <Tooltip label="Clear">
          <ActionIcon
            size={ICON_SIZE}
            variant="transparent"
            onClick={handleClear}
            color="gray"
          >
            <IconX />
          </ActionIcon>
        </Tooltip>
      )}
      <IconChevronDown
        size={ICON_SIZE}
        stroke={ICON_STROKE}
        onClick={handleToggleOpen}
      />
    </Group>
  )

  const currentHoveredItem = hoveredValue
    ? allItems.find((i) => i.value === hoveredValue)
    : selectedItem

  return (
    <Grid gutter="md">
      <Grid.Col span={6}>
        <Popover
          opened={isOpen}
          onChange={setIsOpen}
          position="bottom-start"
          width={width}
          trapFocus={false}
          withinPortal={false}
        >
          <Popover.Target>
            <Box>
              <Text size="sm" fw={500}>
                {label}
              </Text>
              <Box onClick={handleInputClick}>
                <TextInput
                  ref={inputRef}
                  w={inputWidth}
                  value={isSearching ? searchQuery : selectedItem?.label || ""}
                  onChange={handleInputChange}
                  onFocus={(e) => {
                    setIsOpen(true)
                    setIsFocused(true)
                    requestAnimationFrame(() => e.target.select())
                  }}
                  onBlur={() => setIsFocused(false)}
                  onMouseDown={(e) => !isFocused && e.preventDefault()}
                  onKeyDown={handleInputKeyDown}
                  rightSectionWidth={45}
                  rightSection={defaultRightSection}
                  leftSection={defaultLeftSection}
                  placeholder={placeholder}
                />
              </Box>
            </Box>
          </Popover.Target>
          <Popover.Dropdown p={0} bg="surfaceCard">
            <Box mih={200}>
              {showGroupPills && (
                <GroupPills
                  sortedGroupByOptions={options}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={handleGroupChange}
                  hasSuggestedAxes={hasSuggestedItems}
                  showAllPill={showAllPill}
                />
              )}
              <Divider color="border" />
              <Flex style={{ height: dropdownHeight }}>
                <Box
                  flex={1}
                  p="md"
                  style={{ overflow: "auto" }}
                  ref={scrollContainerRef}
                  onKeyDown={handleInputKeyDown}
                >
                  <Stack gap={0}>
                    {selectedGroup === "Suggested" && suggestedQuery ? (
                      <QueryWrapper query={suggestedQuery} defaultData={[]}>
                        {() => <>{filteredItems.map(renderDropDownItem)}</>}
                      </QueryWrapper>
                    ) : (
                      filteredItems.map(renderDropDownItem)
                    )}
                    {selectedGroup !== "Suggested" &&
                      filteredItems.length === 0 && (
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          No matching items found
                        </Text>
                      )}
                  </Stack>
                </Box>
                {renderRightPanel && (
                  <Box
                    flex={1}
                    bg="surfaceGround"
                    style={{
                      overflow: "auto",
                      borderLeft: "1px solid var(--mantine-color-border-0)",
                    }}
                  >
                    {renderRightPanel(currentHoveredItem)}
                  </Box>
                )}
              </Flex>
            </Box>
          </Popover.Dropdown>
        </Popover>
      </Grid.Col>
    </Grid>
  )
}

export default BaseDropdownSelector
